import React from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import {
  Button,
  Col,
  DatePicker,
  Empty,
  Image,
  Progress,
  Row,
  Segmented,
  Select,
  Spin,
  Table,
  Tag
} from 'antd';
import { CloseOutlined, RightOutlined, UserOutlined, WarningFilled } from '@ant-design/icons';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from 'recharts';
import { FREQUENCY_OPTIONS, photoUrl, pressable } from 'src/constants/checklists';
import {
  dailySeries,
  matchesOutcome,
  runProgress,
  runState,
  runSubtitle,
  summariseHistory
} from 'src/constants/checklistRuns';
import { historyByPerson, peopleInRuns, scopeToPerson } from 'src/constants/checklistTeam';

const { RangePicker } = DatePicker;

const GREEN = '#2f8a4c';
const RED = '#c8442b';
const AMBER = '#b9770e';
const MUTED = '#5b6660';
const LINE = '#e3ebe5';

// Long ranges pull every run with its full answers; keep requests sensible.
export const MAX_RANGE_DAYS = 62;

// Rows shown in each "worst first" list before "See all".
const TOP = 5;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6em;
  margin-bottom: 1em;
  .ant-select {
    min-width: 12em;
  }
  @media (max-width: 575px) {
    .ant-picker,
    .ant-select {
      flex: 1 1 100%;
    }
  }
`;

const Scope = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  margin-bottom: 1em;
  padding: 0.35em 0.5em 0.35em 0.9em;
  border-radius: 999px;
  background: #eaf6ee;
  color: #1f5e34;
  font-weight: 600;
  font-size: 13.5px;
`;

const Tile = styled.div`
  background: #fff;
  border: 1px solid ${LINE};
  border-radius: 14px;
  padding: 0.9em 1.1em;
  height: 100%;
  cursor: pointer;
  &:hover {
    border-color: #b9d8c3;
  }

  .label {
    color: ${MUTED};
    font-size: 12.5px;
    font-weight: 600;
  }
  .value {
    font-family: Nunito, sans-serif;
    font-weight: 800;
    font-size: 30px;
    line-height: 1.15;
    font-variant-numeric: tabular-nums;
  }
  .detail {
    color: ${MUTED};
    font-size: 12.5px;
  }
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid ${LINE};
  border-radius: 14px;
  padding: 0.9em 1em 0.6em;
  height: 100%;

  .head {
    display: flex;
    align-items: center;
    gap: 0.6em;
    margin-bottom: 0.4em;
  }
  .title {
    flex: 1;
    font-family: Nunito, sans-serif;
    font-weight: 800;
    font-size: 15px;
  }
  .legend {
    display: flex;
    flex-wrap: wrap;
    gap: 0.9em;
    color: ${MUTED};
    font-size: 12px;
  }
  .legend i {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 2px;
    margin-right: 0.35em;
  }
  .hint {
    color: ${MUTED};
    font-size: 12px;
    margin-top: 0.4em;
  }
`;

const ListRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75em;
  min-height: 48px;
  padding: 0.35em 0.25em;
  border-top: 1px solid #eef2ef;
  cursor: pointer;
  &:hover {
    background: #f7faf8;
  }
  &:focus-visible {
    outline: 2px solid ${GREEN};
    outline-offset: -2px;
  }
  .who {
    flex: 0 1 40%;
    min-width: 0;
  }
  .who div:first-child {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sub {
    color: ${MUTED};
    font-size: 12px;
  }
  .bar {
    flex: 1;
    min-width: 5em;
  }
  .flags {
    display: flex;
    gap: 0.3em;
    flex: none;
  }
`;

const Issue = styled.div`
  box-sizing: border-box;
  display: flex;
  gap: 0.9em;
  width: 100%;
  padding: 0.8em 1em;
  background: #fff;
  border: 1px solid ${LINE};
  border-radius: 12px;
  margin-bottom: 0.5em;
  cursor: pointer;
  box-shadow: inset 4px 0 0 ${RED};

  &:hover {
    border-color: #e9b7ab;
  }
  &:focus-visible {
    outline: 2px solid ${GREEN};
    outline-offset: 2px;
  }
  .text {
    flex: 1;
    min-width: 0;
  }
  .what {
    font-weight: 700;
  }
  .where {
    color: ${MUTED};
    font-size: 12.5px;
  }
  .note {
    margin-top: 0.25em;
    font-size: 13.5px;
  }
`;

const toneFor = rate =>
  rate === null || rate === undefined ? MUTED : rate >= 90 ? GREEN : rate >= 70 ? AMBER : RED;

const rateText = rate => (rate === null || rate === undefined ? '—' : `${rate}%`);

const presets = () => {
  const today = dayjs();
  const yesterday = today.subtract(1, 'day');
  // Weeks run Monday to Sunday.
  const monday = today.subtract((today.day() + 6) % 7, 'day');
  const lastMonth = today.subtract(1, 'month');
  return [
    { label: 'Today', value: [today, today] },
    { label: 'Yesterday', value: [yesterday, yesterday] },
    { label: 'This week', value: [monday, today] },
    { label: 'Last week', value: [monday.subtract(7, 'day'), monday.subtract(1, 'day')] },
    { label: 'Last 30 days', value: [today.subtract(29, 'day'), today] },
    { label: 'This month', value: [today.startOf('month'), today] },
    { label: 'Last month', value: [lastMonth.startOf('month'), lastMonth.endOf('month')] }
  ];
};

// The list under the dashboard: issues, or checklists by how they ended.
const LIST_VIEWS = [
  { value: 'issues', label: 'Issues' },
  { value: 'outstanding', label: 'Outstanding', empty: 'Nothing outstanding in this period.' },
  { value: 'done', label: 'Done', empty: 'Nothing finished in this period.' },
  { value: 'late', label: 'Late', empty: 'Nothing finished late in this period.' },
  { value: 'missed', label: 'Missed', empty: 'Nothing missed in this period.' },
  { value: 'all', label: 'All', empty: 'No checklists in this period.' }
];

const ListSwitch = styled.div`
  margin: 1.6em 0 0.8em;
  overflow-x: auto;
  scroll-margin-top: 72px;
  -webkit-overflow-scrolling: touch;
`;

const SERIES = [
  { key: 'onTime', label: 'On time', color: GREEN },
  { key: 'late', label: 'Late', color: AMBER },
  { key: 'missed', label: 'Missed', color: RED },
  { key: 'open', label: 'Not due yet', color: '#cfd8d2' }
];

// One "worst first" row: name, completion bar, and red flags.
const RateRow = ({ title, sub, row, onClick }) => (
  <ListRow {...pressable(onClick)}>
    <span className="who">
      <div>{title}</div>
      {sub && <div className="sub">{sub}</div>}
    </span>
    <span className="bar">
      {row.completionRate === null ? (
        <span className="sub">Not due yet</span>
      ) : (
        <Progress
          percent={row.completionRate}
          size="small"
          strokeColor={toneFor(row.completionRate)}
          format={() => `${row.completionRate}%`}
        />
      )}
    </span>
    <span className="flags">
      {row.missed > 0 && <Tag color="red">{row.missed} missed</Tag>}
      {row.critical > 0 && (
        <Tag color="red" icon={<WarningFilled />}>
          {row.critical}
        </Tag>
      )}
    </span>
    <RightOutlined style={{ color: MUTED }} />
  </ListRow>
);

const RankedList = ({ title, rows, render, empty }) => {
  const [all, setAll] = React.useState(false);
  const shown = all ? rows : rows.slice(0, TOP);
  return (
    <Panel>
      <div className="head">
        <span className="title">{title}</span>
        {rows.length > TOP && (
          <Button type="link" size="small" onClick={() => setAll(a => !a)} style={{ padding: 0 }}>
            {all ? 'Show less' : `See all ${rows.length}`}
          </Button>
        )}
      </div>
      {shown.length ? shown.map(render) : <div className="hint">{empty}</div>}
      <div className="hint">Worst first · tap to filter</div>
    </Panel>
  );
};

const HistoryView = ({
  runs = [],
  templates: checklistTemplates = [],
  loading,
  from,
  to,
  onRangeChange,
  now,
  today,
  onOpen
}) => {
  const [person, setPerson] = React.useState(null);
  const [department, setDepartment] = React.useState(null);
  const [checklist, setChecklist] = React.useState(null);
  const [frequency, setFrequency] = React.useState(null);
  const [view, setView] = React.useState('issues');
  const listRef = React.useRef(null);

  const current = now();

  // Runs don't record their schedule, so read it from the checklist itself.
  const frequencyOf = checklistTemplates.reduce((acc, t) => {
    acc[String(t._id)] = (t.schedule && t.schedule.frequency) || 'daily';
    return acc;
  }, {});
  const frequencies = FREQUENCY_OPTIONS.filter(o =>
    runs.some(r => frequencyOf[String(r.templateId)] === o.value)
  );

  // Department, checklist and how often narrow which runs count; a person
  // narrows each run down to their own sections.
  const departments = Array.from(new Set(runs.map(r => r.department).filter(Boolean))).sort();
  const checklists = Array.from(
    runs.reduce((acc, r) => acc.set(r.templateId, r.templateName), new Map()),
    ([value, label]) => ({ value, label })
  ).sort((a, b) => String(a.label).localeCompare(String(b.label)));
  const base = runs.filter(
    r =>
      (!department || r.department === department) &&
      (!checklist || r.templateId === checklist) &&
      (!frequency || frequencyOf[String(r.templateId)] === frequency)
  );
  const people = peopleInRuns(base);
  const scoped = person ? scopeToPerson(base, person) : base;

  const { totals, templates, issues } = summariseHistory(scoped, current, today);
  const peopleRows = person ? [] : historyByPerson(base, current, today);
  const series = dailySeries(scoped, from, to, current, today);
  const byId = runs.reduce((acc, r) => {
    acc[r._id] = r;
    return acc;
  }, {});

  const listed = scoped.filter(r => matchesOutcome(r, view, current, today));
  const listView = LIST_VIEWS.find(v => v.value === view) || LIST_VIEWS[0];
  const countFor = value =>
    value === 'issues'
      ? issues.length
      : scoped.filter(r => matchesOutcome(r, value, current, today)).length;
  // Tiles jump to the matching list.
  const showList = value => {
    setView(value);
    if (listRef.current && listRef.current.scrollIntoView) {
      listRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const personName = person && (people.find(p => p.id === person) || {}).name;
  const checklistName = checklist && (checklists.find(c => c.value === checklist) || {}).label;
  const frequencyName = frequency && (FREQUENCY_OPTIONS.find(o => o.value === frequency) || {}).label;
  const scopeParts = [checklistName, frequencyName && `${frequencyName} checklists`].filter(Boolean);
  const showPeople = !person && peopleRows.length > 1;
  const showChecklists = !checklist && templates.length > 0;

  const runColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 110,
      render: date => dayjs(date).format('ddd D MMM'),
      sorter: (a, b) => String(a.date).localeCompare(String(b.date)),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Checklist',
      key: 'name',
      render: (_, run) => (
        <>
          <div style={{ fontWeight: 600 }}>{run.templateName}</div>
          {runSubtitle(run) && <div style={{ color: MUTED, fontSize: 12.5 }}>{runSubtitle(run)}</div>}
        </>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: 170,
      render: (_, run) => {
        if (run.partDone) return <Tag color="green">Their part done</Tag>;
        const state = runState(run, current);
        const color =
          state.tone === 'success'
            ? 'green'
            : state.tone === 'warning'
            ? 'orange'
            : state.tone === 'error'
            ? 'red'
            : 'default';
        // A run from a past day that never got finished reads as missed.
        const missed = run.status !== 'done' && today && run.date < today && !run.dueAt;
        return <Tag color={missed ? 'red' : color}>{missed ? 'Not finished' : state.label}</Tag>;
      }
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 140,
      render: (_, run) => {
        const p = runProgress(run);
        return (
          <Progress
            percent={p.percent}
            size="small"
            strokeColor={run.status === 'done' || run.partDone ? GREEN : AMBER}
            format={() => `${p.answered}/${p.total}`}
          />
        );
      }
    },
    {
      title: 'Completed by',
      key: 'by',
      render: (_, run) =>
        run.status === 'done' ? (
          <>
            {run.completedByName}
            <div style={{ color: MUTED, fontSize: 12.5 }}>
              {dayjs(run.completedAt).format('D MMM HH:mm')}
            </div>
          </>
        ) : (
          <span style={{ color: MUTED }}>—</span>
        )
    }
  ];

  const shortDay = series.length <= 7 ? 'ddd' : 'D MMM';

  return (
    <Spin spinning={Boolean(loading)}>
      <Controls>
        <RangePicker
          value={[dayjs(from), dayjs(to)]}
          presets={presets()}
          allowClear={false}
          disabledDate={d => d.isAfter(dayjs(), 'day')}
          onChange={values => {
            if (!values || !values[0] || !values[1]) return;
            let [start, end] = values;
            // Keep the range to something the server can return quickly.
            if (end.diff(start, 'day') > MAX_RANGE_DAYS) start = end.subtract(MAX_RANGE_DAYS, 'day');
            onRangeChange(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
          }}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Everyone"
          value={person || undefined}
          onChange={value => setPerson(value || null)}
          options={people.map(p => ({ value: p.id, label: p.name }))}
        />
        {departments.length > 1 && (
          <Select
            allowClear
            placeholder="All departments"
            value={department || undefined}
            onChange={value => setDepartment(value || null)}
            options={departments.map(d => ({ value: d, label: d }))}
          />
        )}
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="All checklists"
          value={checklist || undefined}
          onChange={value => setChecklist(value || null)}
          options={checklists}
        />
        {(frequencies.length > 1 || frequency) && (
          <Select
            allowClear
            placeholder="Any schedule"
            value={frequency || undefined}
            onChange={value => setFrequency(value || null)}
            options={frequencies}
          />
        )}
      </Controls>

      {(personName || scopeParts.length > 0) && (
        <Scope>
          {personName && (
            <>
              <UserOutlined /> {personName}'s sections
            </>
          )}
          {personName && scopeParts.length ? ' · ' : ''}
          {scopeParts.join(' · ')}
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            aria-label="Clear filters"
            onClick={() => {
              setPerson(null);
              setChecklist(null);
              setFrequency(null);
            }}
          />
        </Scope>
      )}

      {!scoped.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No checklists in this period."
          style={{ padding: '3em 0' }}
        />
      ) : (
        <>
          <Row gutter={[12, 12]}>
            <Col xs={12} lg={6}>
              <Tile {...pressable(() => showList('done'))}>
                <div className="label">Completed</div>
                <div className="value" style={{ color: toneFor(totals.completionRate) }}>
                  {rateText(totals.completionRate)}
                </div>
                <div className="detail">
                  {totals.done} of {totals.eligible} due
                  {totals.inProgress ? ` · ${totals.inProgress} not due yet` : ''}
                </div>
              </Tile>
            </Col>
            <Col xs={12} lg={6}>
              <Tile {...pressable(() => showList('late'))}>
                <div className="label">On time</div>
                <div className="value" style={{ color: toneFor(totals.onTimeRate) }}>
                  {rateText(totals.onTimeRate)}
                </div>
                <div className="detail">{totals.late} finished late</div>
              </Tile>
            </Col>
            <Col xs={12} lg={6}>
              <Tile {...pressable(() => showList('missed'))}>
                <div className="label">Missed</div>
                <div className="value" style={{ color: totals.missed ? RED : MUTED }}>
                  {totals.missed}
                </div>
                <div className="detail">Past due and not finished</div>
              </Tile>
            </Col>
            <Col xs={12} lg={6}>
              <Tile {...pressable(() => showList('issues'))}>
                <div className="label">Issues raised</div>
                <div className="value" style={{ color: totals.issues ? AMBER : MUTED }}>
                  {totals.issues}
                </div>
                <div className="detail">
                  {totals.critical ? (
                    <span style={{ color: RED, fontWeight: 700 }}>{totals.critical} critical · </span>
                  ) : null}
                  No or out of range
                </div>
              </Tile>
            </Col>
          </Row>

          {series.length > 1 && (
            <Panel style={{ marginTop: 12 }}>
              <div className="head">
                <span className="title">Daily</span>
                <span className="legend">
                  {SERIES.map(s => (
                    <span key={s.key}>
                      <i style={{ background: s.color }} />
                      {s.label}
                    </span>
                  ))}
                </span>
              </div>
              <div style={{ width: '100%', height: 190 }}>
                <ResponsiveContainer>
                  <BarChart data={series} margin={{ top: 8, right: 4, bottom: 0, left: -24 }}>
                    <CartesianGrid vertical={false} stroke="#eef2ef" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={d => dayjs(d).format(shortDay)}
                      tick={{ fontSize: 11, fill: MUTED }}
                      tickLine={false}
                      axisLine={false}
                      interval="preserveStartEnd"
                      minTickGap={8}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: MUTED }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <RechartsTooltip
                      cursor={{ fill: 'rgba(47, 138, 76, 0.06)' }}
                      labelFormatter={d => dayjs(d).format('dddd D MMM')}
                      formatter={(value, key) => [value, (SERIES.find(s => s.key === key) || {}).label]}
                    />
                    {SERIES.map((s, i) => (
                      <Bar
                        key={s.key}
                        dataKey={s.key}
                        stackId="day"
                        fill={s.color}
                        maxBarSize={28}
                        radius={i === SERIES.length - 1 ? [4, 4, 0, 0] : 0}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          )}

          {(showPeople || showChecklists) && (
            <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
              {showPeople && (
                <Col xs={24} lg={showChecklists ? 12 : 24}>
                  <RankedList
                    title="People"
                    rows={peopleRows}
                    empty="No one assigned in this period."
                    render={p => (
                      <RateRow
                        key={p.person.id}
                        title={p.person.name}
                        sub={`${p.runs} checklist${p.runs === 1 ? '' : 's'}`}
                        row={p}
                        onClick={() => setPerson(p.person.id)}
                      />
                    )}
                  />
                </Col>
              )}
              {showChecklists && (
                <Col xs={24} lg={showPeople ? 12 : 24}>
                  <RankedList
                    title="Checklists"
                    rows={templates}
                    empty="No checklists in this period."
                    render={t => (
                      <RateRow
                        key={t.templateId}
                        title={t.name}
                        sub={[t.department, `${t.runs} run${t.runs === 1 ? '' : 's'}`]
                          .filter(Boolean)
                          .join(' · ')}
                        row={t}
                        onClick={() => setChecklist(t.templateId)}
                      />
                    )}
                  />
                </Col>
              )}
            </Row>
          )}

          <ListSwitch ref={listRef}>
            <Segmented
              value={view}
              onChange={setView}
              options={LIST_VIEWS.map(v => ({ value: v.value, label: `${v.label} (${countFor(v.value)})` }))}
            />
          </ListSwitch>

          {view === 'issues' ? (
            issues.length ? (
              issues.map(issue => (
                <Issue key={issue.key} {...pressable(() => onOpen(byId[issue.runId]))}>
                  <span className="text">
                    <div className="what">
                      {issue.critical && (
                        <Tag color="red" icon={<WarningFilled />} style={{ marginRight: '0.4em' }}>
                          Critical
                        </Tag>
                      )}
                      {issue.itemLabel}
                    </div>
                    <div className="where">
                      {dayjs(issue.date).format('ddd D MMM')} · {issue.templateName}
                      {issue.subtitle ? ` · ${issue.subtitle}` : ''} · {issue.sectionTitle}
                    </div>
                    {issue.type === 'reading' && issue.value !== undefined && (
                      <Tag color="red" style={{ marginTop: '0.3em' }}>
                        {issue.value}
                        {issue.unit}
                      </Tag>
                    )}
                    {issue.note && <div className="note">{issue.note}</div>}
                    {issue.byName && <div className="where">{issue.byName}</div>}
                  </span>
                  {issue.photo && (
                    <span onClick={e => e.stopPropagation()} role="presentation">
                      <Image
                        src={photoUrl(issue.photo)}
                        width={64}
                        height={64}
                        style={{ borderRadius: 8, objectFit: 'cover' }}
                        alt={`Photo for ${issue.itemLabel}`}
                      />
                    </span>
                  )}
                </Issue>
              ))
            ) : (
              <div style={{ color: GREEN, fontWeight: 600, padding: '0.5em 0' }}>
                No issues raised in this period.
              </div>
            )
          ) : !listed.length ? (
            <div style={{ color: view === 'outstanding' || view === 'missed' ? GREEN : MUTED, fontWeight: 600, padding: '0.5em 0' }}>
              {listView.empty}
            </div>
          ) : (
            <Table
              size="small"
              rowKey="_id"
              columns={runColumns}
              dataSource={listed}
              pagination={{ pageSize: 25, hideOnSinglePage: true }}
              scroll={{ x: 'max-content' }}
              onRow={run => ({ onClick: () => onOpen(byId[run._id] || run), style: { cursor: 'pointer' } })}
            />
          )}
        </>
      )}
    </Spin>
  );
};

export default HistoryView;
